/**
 * Analytics tracking guard - prevents tracking in development and for staff/testing scenarios
 * All GA4 tracking must go through this guard to respect user privacy and prevent dev pollution
 */

/**
 * Internal helper for checking basic tracking conditions
 * @param {string} environment - Node environment (for testing)
 * @returns {boolean} true if basic conditions are met
 */
const checkBasicTrackingConditions = (environment = process.env.NODE_ENV) => {
  try {
    // 1. Environment check - block non-production
    if (environment !== 'production') {
      return false;
    }

    // 2. Hostname check - only allow production domain
    const hostname = window.location.hostname;
    const productionDomains = ['salarytools.co.uk', 'www.salarytools.co.uk'];
    if (!productionDomains.includes(hostname)) {
      return false;
    }

    // 3. Do Not Track browser setting
    if (navigator.doNotTrack === '1' || window.doNotTrack === '1') {
      return false;
    }

    // 4. Staff testing flag check (cookie or localStorage)
    if (hasStaffTestingFlag()) {
      return false;
    }

    // 5. URL override check - if present, set persistent flag and block
    if (hasUrlOverride()) {
      setStaffTestingFlag();
      return false;
    }

    return true;

  } catch (error) {
    // On any error, fail safe by blocking tracking
    console.debug('checkBasicTrackingConditions() error, blocking tracking:', error);
    return false;
  }
};

/**
 * Check if basic pageview tracking should be allowed (without consent)
 * Returns false (no tracking) when ANY condition is true:
 * - Not production environment
 * - Not production hostname (salarytools.co.uk)  
 * - Do Not Track browser setting enabled
 * - Staff testing cookie/localStorage flag exists
 * - URL override parameter present (?no_analytics=1)
 * 
 * @param {string} environment - Node environment (for testing, defaults to process.env.NODE_ENV)
 * @returns {boolean} true if pageview tracking allowed, false if blocked
 */
export const shouldTrackPageViews = (environment = process.env.NODE_ENV) => {
  return checkBasicTrackingConditions(environment);
};

/**
 * Check if full event tracking should be allowed (requires consent)
 * Uses same base checks as pageviews but also requires user consent
 * 
 * @param {boolean} hasConsent - Whether user has given analytics consent
 * @param {string} environment - Node environment (for testing, defaults to process.env.NODE_ENV)
 * @returns {boolean} true if event tracking allowed, false if blocked
 */
export const shouldTrackEvents = (hasConsent = false, environment = process.env.NODE_ENV) => {
  // First check if basic conditions are met
  if (!checkBasicTrackingConditions(environment)) {
    return false;
  }

  // Additional check: user must have given explicit consent for events
  if (!hasConsent) {
    return false;
  }

  return true;
};

/**
 * Legacy function - Check if analytics tracking should be allowed
 * Returns false (no tracking) when ANY condition is true:
 * - Not production environment
 * - Not production hostname (salarytools.co.uk)  
 * - Do Not Track browser setting enabled
 * - Staff testing cookie/localStorage flag exists
 * - URL override parameter present (?no_analytics=1)
 * 
 * @returns {boolean} true if tracking allowed, false if blocked
 */
export const shouldTrack = () => {
  try {
    // 1. Environment check - block non-production
    if (process.env.NODE_ENV !== 'production') {
      return false;
    }

    // 2. Hostname check - only allow production domain
    const hostname = window.location.hostname;
    const productionDomains = ['salarytools.co.uk', 'www.salarytools.co.uk'];
    if (!productionDomains.includes(hostname)) {
      return false;
    }

    // 3. Do Not Track browser setting
    if (navigator.doNotTrack === '1' || window.doNotTrack === '1') {
      return false;
    }

    // 4. Staff testing flag check (cookie or localStorage)
    if (hasStaffTestingFlag()) {
      return false;
    }

    // 5. URL override check - if present, set persistent flag and block
    if (hasUrlOverride()) {
      setStaffTestingFlag();
      return false;
    }

    // All checks passed - tracking allowed
    return true;

  } catch (error) {
    // On any error, fail safe by blocking tracking
    console.debug('shouldTrack() error, blocking tracking:', error);
    return false;
  }
};

/**
 * Check if staff testing flag exists in cookie or localStorage
 * @returns {boolean}
 */
const hasStaffTestingFlag = () => {
  // Check localStorage first
  try {
    if (localStorage.getItem('staff_testing') === 'true') {
      return true;
    }
  } catch (error) {
    // Ignore localStorage errors (private browsing, etc.)
  }

  // Check cookie
  return document.cookie
    .split(';')
    .some(cookie => cookie.trim().startsWith('staff_testing=true'));
};

/**
 * Check if URL contains no_analytics override parameter
 * @returns {boolean}
 */
const hasUrlOverride = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('no_analytics') === '1';
};

/**
 * Set staff testing flag in both localStorage and cookie for persistence
 * Called when ?no_analytics=1 is detected
 */
const setStaffTestingFlag = () => {
  try {
    // Set in localStorage (preferred)
    localStorage.setItem('staff_testing', 'true');
  } catch (error) {
    // Ignore localStorage errors
  }

  // Set persistent cookie (1 year expiration)
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `staff_testing=true; expires=${expires.toUTCString()}; path=/; SameSite=Strict; Secure`;
  
  console.info('Analytics tracking disabled for this browser. Clear cookies/localStorage or use ?analytics=1 to re-enable.');
};

/**
 * Get debug configuration for GA4
 * Enables debug_mode when tracking is blocked and not in production
 * @returns {object} GA4 debug configuration
 */
export const getDebugConfig = () => {
  const trackingBlocked = !shouldTrack();
  const isNonProd = process.env.NODE_ENV !== 'production';
  
  return {
    debug_mode: trackingBlocked && isNonProd,
    // Send debug events to GA4 DebugView when tracking is blocked in non-prod
    // This helps developers test tracking without polluting production analytics
  };
};

/**
 * Clear staff testing flag to re-enable tracking
 * Utility function for testing/development
 */
export const clearStaffTestingFlag = () => {
  try {
    localStorage.removeItem('staff_testing');
  } catch (error) {
    // Ignore localStorage errors
  }
  
  // Clear cookie by setting expired date
  document.cookie = 'staff_testing=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict; Secure';
  
  console.info('Analytics tracking re-enabled for this browser.');
};

/**
 * Check current tracking status for debugging
 * @returns {object} Status information
 */
export const getTrackingStatus = () => {
  return {
    shouldTrack: shouldTrack(),
    environment: process.env.NODE_ENV,
    hostname: window.location.hostname,
    doNotTrack: navigator.doNotTrack || window.doNotTrack,
    staffTesting: hasStaffTestingFlag(),
    urlOverride: hasUrlOverride(),
    debugMode: getDebugConfig().debug_mode
  };
};